//@ts-nocheck
import NoTripsFound from '../NoTripsFound/NoTripsFound';
import DeleteModal from '../../Trip/DeleteModal/DeleteModal';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Copy, Trash } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { ErrorToast } from '../../../utils/alerts';
import { cloneTrip, deleteTrip, getTrips } from '../../../services/trip/trip';
import { XGButton, XGInputSearch, XGSelect } from 'xg-cl';
import { format as formatDate, utcToZonedTime } from 'date-fns-tz';
import { getLocalTenantData } from '../../../utils/localTenantData';
import { buildTripsQueryParams } from '../utils/buildTripsQueryParams';
import { CANCELLED_STATUS } from '../../../constants/booking';
import * as PropTypes from 'prop-types';
import './TripsCardList.css';

// Skeleton Card Component
const SkeletonCard = () => (
  <Box className='trip-card trip-card--skeleton'>
    <Box className='trip-card__content'>
      <Box className='trip-card__header'>
        <Box className='skeleton skeleton--date-badge'></Box>
        <Box className='skeleton skeleton--title'></Box>
        <Box className='skeleton skeleton--status'></Box>
      </Box>
    </Box>
    <Box className='trip-card__stats'>
      <Box className='trip-card__stat'>
        <Box className='skeleton skeleton--stat-label'></Box>
      </Box>
      <Box className='trip-card__stat'>
        <Box className='skeleton skeleton--stat-label'></Box>
      </Box>
    </Box>
    <Box className='trip-card__actions'>
      <Box className='skeleton skeleton--action-button'></Box>
      <Box className='skeleton skeleton--action-button'></Box>
    </Box>
  </Box>
);
const ITEMS_PER_PAGE = 15;

/**
 * A component for displaying a list of trips.
 * It handles loading more trips when the user reaches the end of the list.
 * It also handles the case when there are no trips to display.
 */
const TripsCardList = ({
  initialTrips,
  totalRecordsCount,
  onSortChange,
  sortModel,
  loading: parentLoading,
  tripStatuses,
  enableDisplayStatuses,
  onChangeSearchInput,
  onClickSearchInput,
  onKeyDownSearchInput,
  searchInput,
  filters,
  onTripDeleted
}) => {
  const { t } = useTranslation(['trips', 'common', 'trip', 'errors']);
  const navigate = useNavigate();
  const observerTarget = useRef(null);
  // Accumulated trips
  const [allTrips, setAllTrips] = useState(initialTrips || []);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const tenantData = getLocalTenantData();
  const currencySymbol = tenantData?.sellingcurrency?.currencysymbol;

  //  Reset trips when initialTrips changes (new filter/search/sort)
  useEffect(() => {
    setAllTrips(initialTrips || []);
    setCurrentPage(1);
    setReachedEnd(false);
  }, [initialTrips]);

  // Clear trips when parent loading starts
  useEffect(() => {
    if (parentLoading) setAllTrips([]);
  }, [parentLoading]);

  // Calculate if there are more items to load
  const hasMore = !reachedEnd && allTrips?.length < totalRecordsCount;

  // Sort options mapping
  const sortOptions = [
    {
      value: 'startDate-desc',
      label: t('startDateLatestFirst', 'Start Date (Latest First)')
    },
    {
      value: 'startDate-asc',
      label: t('startDateEarliestFirst', 'Start Date (Earliest First)')
    },

    { value: 'title-asc', label: t('titleAZ', 'Title (A-Z)') },
    { value: 'title-desc', label: t('titleZA', 'Title (Z-A)') },
    {
      value: 'lastModified-desc',
      label: t('lastModifiedNewestFirst', 'Last Modified (Newest First)')
    },
    {
      value: 'lastModified-asc',
      label: t('lastModifiedOldestFirst', 'Last Modified (Oldest First)')
    }
  ];

  const getCurrentSortValue = () => {
    if (!sortModel || sortModel.length === 0) return 'lastModified-desc';
    const { field, sort } = sortModel[0];
    return `${field}-${sort}`;
  };

  const getCurrentSortValueLabel = () => {
    const currentValue = getCurrentSortValue();
    const option = sortOptions?.find((opt) => opt?.value === currentValue);
    return option ? option?.label : '';
  };

  const handleSortChange = (event, optElement) => {
    const [field, sort] = optElement.props.name.split('-');
    onSortChange([{ field, sort }]);
  };

  const loadMoreTrips = useCallback(async () => {
    if (isLoadingMore || !hasMore || parentLoading || isDeleting) return;

    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      // Calculate skip based on current loaded items, not page
      const skip = allTrips.length;

      const queryParams = buildTripsQueryParams({
        filters,
        tripStatuses,
        enableDisplayStatuses,
        take: ITEMS_PER_PAGE,
        skip
      });

      const response = await getTrips(`?${queryParams.toString()}`);

      if (response?.data?.trips && response.data.trips.length > 0) {
        // Transform new trips to match the expected format
        const newTrips = response?.data?.trips.map((trip) => {
          const cityName = trip?.city?.cityname || '';
          const countryName = trip?.country?.countryname || '';
          const destination =
            cityName && countryName
              ? `${cityName}, ${countryName}`
              : cityName || countryName || '-';

          const newStartDate = trip?.startdate
            ? trip?.startdate.split('T')[0].toString()
            : null;
          return {
            ...trip,
            id: trip?.tripid,
            title: trip?.tripname,
            destination: destination,
            startDate: trip?.startdate
              ? formatDate(
                  utcToZonedTime(new Date(newStartDate), 'UTC'),
                  'dd MMM yyyy',
                  { timeZone: 'UTC' }
                )
              : '-',
            endDate: trip.enddate ? formatDate(trip.enddate) : '-',
            status:
              trip?.tripstatusdisplaycode ||
              tripStatuses.find(
                (tripstatus) => tripstatus.tripstatusid === trip?.tripstatusid
              )?.tripstatuscode ||
              '',
            passengersCount: parseInt(trip?.passengersCount) || 0,
            maxPassengers: parseInt(trip?.capacity) || 0,
            revenue: trip.amountReceivedTotal || 0,
            maxRevenue: trip.tripTotal || 0,
            lastModified: trip?.modifieddatetime
              ? formatDate(new Date(trip?.modifieddatetime), 'dd LLL yyyy')
              : '-',
            createdByUser: trip?.createdByUser,
            paymentSchedules: trip?.paymentSchedules || [],
            updatedByUser: trip?.updatedByUser
          };
        });

        // Append new trips to existing ones
        setAllTrips((prevTrips) => {
          // Avoid duplicates by checking IDs
          const existingIds = new Set(prevTrips.map((trip) => trip.id));
          const uniqueNewTrips = newTrips.filter(
            (trip) => !existingIds.has(trip.id)
          );
          return [...prevTrips, ...uniqueNewTrips];
        });

        setCurrentPage(nextPage);
      } else {
        // Prevent repeated observer triggers when backend has no more data.
        setReachedEnd(true);
      }
    } catch (error) {
      console.error('Error loading more trips:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    parentLoading,
    isDeleting,
    allTrips.length,
    filters,
    currentPage,
    tripStatuses,
    enableDisplayStatuses
  ]);

  // Setup Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (
          entry.isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !parentLoading &&
          !isDeleting
        ) {
          loadMoreTrips();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px'
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget && hasMore) {
      // Only observe if there are more items
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [
    loadMoreTrips,
    hasMore,
    isLoadingMore,
    parentLoading,
    isDeleting,
    allTrips.length
  ]);

  const openTrip = (tripId) => {
    navigate(`/trips/${tripId}`);
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'proposal') return 'trip-card__status--proposal';
    if (statusLower === 'confirmed') return 'trip-card__status--confirmed';
    if (statusLower === 'active') return 'trip-card__status--active';
    if (statusLower === 'complete') return 'trip-card__status--complete';
    if (statusLower === 'cancelled') return 'trip-card__status--cancelled';
    if (statusLower === 'archived') return 'trip-card__status--archived';
    if (statusLower === 'traveling') return 'trip-card__status--traveling';
    return '';
  };

  const formatPassengerCount = (current, max) => {
    if (max === null || max === undefined || max === 0) {
      return ``;
    }
    return `/${max}`;
  };

  const getOverDueCount = (trip) => {
    const { paymentSchedules = [] } = trip;
    let overdueCount = 0;

    paymentSchedules?.forEach((schedule) => {
      const {
        amountReceived = 0,
        balance = 0,
        paymentSchedule,
        bookingStatus
      } = schedule;

      // Get payment schedule data
      const evaluatedEntries = paymentSchedule?.evaluatedEntries || [];
      const summary = paymentSchedule?.summary || {};

      const pastDueEntries = evaluatedEntries.filter(
        (entry) => entry.status === 'past_due'
      );

      const toCents = (value) =>
        Math.round((Number(value || 0) + Number.EPSILON) * 100);

      // Calculate total expected to be received by today (all past due payments, excluding due_today)
      const totalExpectedByTodayCents = toCents(summary.pastDueTotal || 0);

      // Parse the formatted amountReceived string to get the numeric value
      // Format is like "$ 759.75 USD" or "$759.75 USD"
      const amountReceivedNumeric =
        typeof amountReceived === 'string'
          ? parseFloat(amountReceived.replace(/[^0-9.-]+/g, '')) || 0
          : amountReceived || 0;
      const amountReceivedCents = toCents(amountReceivedNumeric);
      const balanceCents = toCents(balance);

      // Calculate actual overdue: what should have been received minus what was received
      const actualOverdueCents = Math.max(
        0,
        totalExpectedByTodayCents - amountReceivedCents
      );

      // Only show overdue if there's actually an overdue amount
      const hasPastDue = actualOverdueCents > 0;

      if (
        balanceCents > 0 &&
        bookingStatus?.statuscode != CANCELLED_STATUS &&
        hasPastDue &&
        pastDueEntries?.length > 0
      ) {
        overdueCount += 1;
      }
    });

    return overdueCount;
  };

  const handleClickCopyTrip = async (id) => {
    setIsCopying(true);
    setSelectedTripId(id);
    try {
      const { data } = await cloneTrip(id);
      navigate(`/trips/${data?.newtripid}`);
    } catch (error) {
      ErrorToast(t('errors:tripCouldNotBeCopied'));
    } finally {
      setIsCopying(false);
    }
  };

  const handleDeleteTripClick = async (evt, id) => {
    evt.stopPropagation();
    evt.preventDefault();
    setSelectedTripId(id);
    setIsDeleteModalOpen(true);
  };

  const handleOnConfirmRemoval = async () => {
    try {
      setIsDeleting(true);
      await deleteTrip(selectedTripId);
      if (typeof onTripDeleted === 'function') {
        onTripDeleted();
      }
    } catch (error) {
      ErrorToast(t('errors:tripCouldNotBeDeleted'));
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleOnCancelRemoval = () => {
    setIsDeleteModalOpen(false);
  };

  const getCreatedByUser = (trip) => {
    const user = trip?.createdByUser;
    return `${user?.firstname || ''} ${user?.lastname || ''}`.trim();
  };

  const formatCurrencyAbbrev = (value, currencySymbol = '$') => {
    if (value == null || isNaN(value)) return `${currencySymbol}0`;

    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    // truncate (no round-up) to 1 decimal, then drop trailing .0
    const format = (num) => {
      const truncated = Math.floor(num * 10) / 10;
      const str = truncated.toFixed(1);
      return str.endsWith('.0') ? str.slice(0, -2) : str;
    };

    if (abs >= 1_000_000_000)
      return `${sign}${currencySymbol}${format(abs / 1_000_000_000)}B`;
    if (abs >= 1_000_000)
      return `${sign}${currencySymbol}${format(abs / 1_000_000)}M`;
    if (abs >= 1_000) return `${sign}${currencySymbol}${format(abs / 1_000)}K`;

    return `${sign}${currencySymbol}${abs.toLocaleString()}`;
  };

  return (
    <Box className='trips-card-list'>
      {/* Header with search and sort */}
      <Box className='trips-card-list__header'>
        <Box style={{ width: '450px' }}>
          <XGInputSearch
            className='trips-inputFilter'
            id='trips-card__filter-input'
            placeholder={t('searchByTitle')}
            name='search'
            type='text'
            onChange={onChangeSearchInput}
            onClick={onClickSearchInput}
            onKeyDown={onKeyDownSearchInput}
            value={searchInput}
          />
        </Box>
        <Box className='trips-card-list__sort'>
          <Typography
            variant='subtitle1'
            className='trips-card-list__sort-label'
          >
            {t('sortBy', 'Sort by:')}
          </Typography>
          <Box className='trips-card-list__sort-select-container'>
            <XGSelect
              id='sort-select-List'
              className='trips-card-list__sort-select'
              name='tripSortValue'
              size='small'
              value={getCurrentSortValueLabel()}
              menuItems={sortOptions ?? []}
              onChange={(event, optElement) => {
                handleSortChange(event, optElement);
              }}
              disabled={allTrips?.length === 0 && !parentLoading}
            />
          </Box>
        </Box>
      </Box>

      {/* Cards Container */}
      <Box className='trips-card-list__container'>
        {parentLoading ? (
          <>
            {/* Show 3 skeleton cards while loading */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <React.Fragment key={`skeleton-${i}`}>
                <SkeletonCard />
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            {allTrips?.map((trip) => (
              <React.Fragment key={trip.id}>
                <Box
                  className='trip-card'
                  onClick={() => openTrip(trip.id)}
                  role='button'
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') openTrip(trip.id);
                  }}
                >
                  {/* Main Content */}
                  <Box className='trip-card__content'>
                    {/* Date Badge, Title and Status - All in one row */}
                    <Box className='trip-card__header'>
                      {trip.startDate && trip.startDate !== '-' && (
                        <Box className='trip-card__date-badge'>
                          {trip.startDate}
                        </Box>
                      )}
                      <h3 className='trip-card__title'>
                        <a
                          href={`/trips/${trip?.id}`}
                          onClick={(evt) => {
                            evt.stopPropagation();
                          }}
                        >
                          {trip?.title}
                        </a>
                      </h3>
                    </Box>

                    {/* Metadata Row - Second row */}
                    <Box className='trip-card__metadata'>
                      {trip?.destination && trip?.destination !== '-' && (
                        <span className='trip-card__destination'>
                          {trip.destination}
                        </span>
                      )}

                      {trip?.lastModified && (
                        <>
                          {trip?.destination && trip?.destination !== '-' && (
                            <span className='trip-card__metadata-separator'>
                              •{' '}
                            </span>
                          )}
                          <span className='trip-card__modified'>
                            {t('modified', 'Modified')}{' '}
                            {trip?.lastModified || ''}
                          </span>
                        </>
                      )}

                      {trip?.createdByUser &&
                        Object.keys(trip?.createdByUser).length > 0 && (
                          <>
                            <span className='trip-card__metadata-separator'>
                              •
                            </span>
                            <span className='trip-card__creator'>
                              {getCreatedByUser(trip)}
                            </span>
                          </>
                        )}

                      {getOverDueCount(trip) > 0 && (
                        <span className='trip-card__overdue'>
                          ⚠️ {getOverDueCount(trip)}{' '}
                          {t('paymentsOverdue', 'payments overdue')}
                        </span>
                      )}
                    </Box>
                  </Box>

                  {/* Stats */}
                  <Box className='trip-card__stats'>
                    <Box className='trip-card__badges'>
                      <span
                        className={`trip-card__status ${getStatusBadgeClass(trip.status)}`}
                      >
                        {trip?.status || ''}
                      </span>
                    </Box>
                    <Box className='trip-card__stat trip-card__stat--left-bordered'>
                      <span className='trip-card__stat-label'>
                        {t('passengers', 'PASSENGERS').toUpperCase()}
                      </span>
                      <span className='trip-card__stat-value'>
                        {trip.passengersCount}
                        <span style={{ color: '#adb5bd' }}>
                          {formatPassengerCount(
                            trip.passengersCount || 0,
                            trip.maxPassengers
                          )}
                        </span>
                      </span>
                    </Box>
                    <Box className='trip-card__stat'>
                      <span className='trip-card__stat-label'>
                        {t('revenue', 'REVENUE').toUpperCase()}
                      </span>
                      <span className='trip-card__stat-value'>
                        {formatCurrencyAbbrev(
                          trip.revenue || 0,
                          currencySymbol
                        )}
                        /
                        <span style={{ color: '#adb5bd' }}>
                          {formatCurrencyAbbrev(
                            trip.maxRevenue || 0,
                            currencySymbol
                          )}
                        </span>
                      </span>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box className='trip-card__actions'>
                    <XGButton
                      data-testid='delete-trip-button'
                      id={`trips-list-delete-button-${trip?.id}`}
                      text={t('common:delete')}
                      size='small'
                      variant='text'
                      type='primary'
                      startIcon={<Trash size={12} />}
                      onClick={(evt) => handleDeleteTripClick(evt, trip?.id)}
                      disabled={isDeleting || isCopying}
                    />
                    <Box sx={{ position: 'relative' }}>
                      <XGButton
                        data-testid='clone-trip-button'
                        id={`trips-list-clone-button-${trip.id}`}
                        text={t('common:copy')}
                        size='small'
                        variant='text'
                        type='primary'
                        startIcon={<Copy size={12} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClickCopyTrip(trip.id);
                        }}
                        disabled={isDeleting || isCopying}
                      />
                      {isCopying && selectedTripId === trip.id && (
                        <CircularProgress
                          id='copy-trip-spinner'
                          size={22}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-11px',
                            marginLeft: '-11px'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </>
        )}

        {/* Loading indicator for load more */}
        {!parentLoading && isLoadingMore && (
          <Box className='trips-card-list__loading'>
            <CircularProgress size={30} />
            <Box className='trips-card-list__loading-text'>
              {t('loadingMoreTrips', 'Loading more trips...')}
            </Box>
          </Box>
        )}

        {/* Intersection observer target */}
        {hasMore && !isLoadingMore && !parentLoading && allTrips.length > 0 && (
          <Box ref={observerTarget} className='trips-card-list__observer' />
        )}

        {/* No more trips message */}
        {!hasMore && allTrips?.length > 0 && (
          <Box className='trips-card-list__end-message'>
            {t('allTripsLoaded', 'All trips loaded')}
            <br />
            <small>
              {t('showingTotal', 'Showing {{count}} of {{total}} trips', {
                count: allTrips.length,
                total: totalRecordsCount
              })}
            </small>
          </Box>
        )}

        {/* Empty state */}
        {allTrips?.length === 0 && !parentLoading && (
          <Box
            style={{
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '10rem'
            }}
          >
            <NoTripsFound />
          </Box>
        )}
      </Box>

      <DeleteModal
        id='delete-trip-modal'
        isOpenModal={isDeleteModalOpen}
        title={t('trip:deleteTrip')}
        onConfirm={handleOnConfirmRemoval}
        onCancel={handleOnCancelRemoval}
        description={`${t('trip:deleteTripConfirmation')} ${t(
          'common:thisActionCannotBeUndone'
        )}`}
        isSubmitting={isDeleting}
      />
    </Box>
  );
};

TripsCardList.propTypes = {
  initialTrips: PropTypes.array.isRequired,
  totalRecordsCount: PropTypes.number.isRequired,
  onSortChange: PropTypes.func.isRequired,
  sortModel: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  filters: PropTypes.object,
  tripStatuses: PropTypes.array,
  enableDisplayStatuses: PropTypes.bool,
  onChangeSearchInput: PropTypes.func,
  onClickSearchInput: PropTypes.func,
  onKeyDownSearchInput: PropTypes.func,
  searchInput: PropTypes.string,
  onTripDeleted: PropTypes.func
};

export default TripsCardList;
